import React, { useState } from 'react';
import { Link, useNavigate, useSearchParams, useParams } from 'react-router-dom';
import { Mail, Lock, Eye, EyeOff, AlertCircle, Globe } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../hooks/use-toast';
import { SUPPORTED_LANGUAGES, DEFAULT_LANGUAGE } from '../components/routing/LanguageWrapper';
import { BRAND } from '../config/brand';
import { STORAGE_KEYS, writeStorageValue } from '../utils/storageKeys';
import './LoginPage.css';

export function LoginPage() {
  const navigate = useNavigate();
  const { lang } = useParams();
  const [searchParams] = useSearchParams();
  const { login } = useAuth();
  const { toast } = useToast();
  const { i18n, t } = useTranslation();
  const persistLanguagePreference = (value) => {
    if (typeof window === 'undefined') {
      return;
    }
    writeStorageValue(window.localStorage, STORAGE_KEYS.language, value);
  };

  const languageOptions = [
    { code: 'en', label: t('settings.general.languages.en'), flag: '🇬🇧' },
    { code: 'it', label: t('settings.general.languages.it'), flag: '🇮🇹' },
    { code: 'es', label: t('settings.general.languages.es'), flag: '🇪🇸' }
  ];
  
  // Get current language from URL or default
  const currentLang = lang && SUPPORTED_LANGUAGES.includes(lang) ? lang : DEFAULT_LANGUAGE;
  
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    rememberMe: false,
  });
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [fieldErrors, setFieldErrors] = useState({});
  const [langMenuOpen, setLangMenuOpen] = useState(false);

  // Sync i18n with URL language
  React.useEffect(() => {
    if (currentLang && i18n.language !== currentLang) {
      i18n.changeLanguage(currentLang);
      persistLanguagePreference(currentLang);
    }
  }, [currentLang, i18n]);

  const handleLanguageChange = (newLang) => {
    i18n.changeLanguage(newLang);
    persistLanguagePreference(newLang);
    // Navigate to the same login page with new language
    navigate(`/${newLang}/login${location.search}`, { replace: true });
    setLangMenuOpen(false);
  };

  const validate = () => {
    const errors = {};
    
    if (!formData.email) {
      errors.email = t('validation.required');
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      errors.email = t('validation.invalidEmail');
    }
    
    if (!formData.password) {
      errors.password = t('validation.required');
    } else if (formData.password.length < 6) {
      errors.password = t('validation.passwordTooShort');
    }
    
    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    
    if (!validate()) return;
    
    setLoading(true);
    
    try {
      const result = await login(formData.email, formData.password, formData.rememberMe);
      
      if (result.success) {
        toast({
          title: t('login.toast.welcomeTitle'),
          description: t('login.toast.signedInAs', { email: result.user.email }),
          variant: 'default',
        });
        
        // Redirect to intended page or language-prefixed dashboard
        const redirect = searchParams.get('redirect');
        if (redirect) {
          // If redirect already has language prefix, use it as-is
          if (SUPPORTED_LANGUAGES.some(l => redirect.startsWith(`/${l}/`))) {
            navigate(redirect);
          } else {
            // Add language prefix to redirect
            navigate(`/${currentLang}${redirect.startsWith('/') ? '' : '/'}${redirect}`);
          }
        } else {
          navigate(`/${currentLang}/dashboard`);
        }
      } else {
        setError(result.error);
      }
    } catch (err) {
      setError(t('errors.generic'));
    } finally {
      setLoading(false);
    }
  };

  const currentLangOption = languageOptions.find(l => l.code === currentLang) || languageOptions[0];

  return (
    <div className="login-page">
      {/* Language Switcher */}
      <div className="login-lang-switcher">
        <button
          className="login-lang-button"
          onClick={() => setLangMenuOpen(!langMenuOpen)}
        >
          <Globe size={18} />
          <span>{currentLangOption.label}</span>
        </button>
        
        {langMenuOpen && (
          <>
            <div 
              className="login-lang-backdrop"
              onClick={() => setLangMenuOpen(false)}
            />
            <div className="login-lang-dropdown">
              {languageOptions.map((langOption) => (
                <button
                  key={langOption.code}
                  className={`login-lang-item ${langOption.code === currentLang ? 'login-lang-item--active' : ''}`}
                  onClick={() => handleLanguageChange(langOption.code)}
                >
                  <span>{langOption.flag}</span>
                  <span>{langOption.label}</span>
                </button>
              ))}
            </div>
          </>
        )}
      </div>

      <div className="login-card">
        {/* Logo */}
        <div className="login-logo">
          <img src="/logo-icon.png" alt={t('login.logoAlt')} className="login-logo-img" />
          <div className="login-logo-wordmark">{BRAND.wordmark}</div>
        </div>

        {/* Title */}
        <h1 className="login-title">{t('login.title')}</h1>
        <p className="login-subtitle">{t('login.subtitle')}</p>

        {/* Error Alert */}
        {error && (
          <div className="login-error-alert">
            <AlertCircle size={20} />
            <span>{error}</span>
            <button
              className="login-error-close"
              onClick={() => setError('')}
              aria-label={t('login.closeError')}
            >
              ×
            </button>
          </div>
        )}

        {/* Form */}
        <form onSubmit={handleSubmit} className="login-form">
          {/* Email */}
          <div className="form-group">
            <label className="form-label">{t('login.emailLabel')}</label>
            <div className="form-input-wrapper">
              <Mail className="form-input-icon" size={20} />
              <input
                type="email"
                className={`form-input ${fieldErrors.email ? 'form-input--error' : ''}`}
                placeholder={t('login.emailPlaceholder')}
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                disabled={loading}
              />
            </div>
            {fieldErrors.email && (
              <p className="form-error">
                <AlertCircle size={12} />
                {fieldErrors.email}
              </p>
            )}
          </div>

          {/* Password */}
          <div className="form-group">
            <label className="form-label">{t('login.passwordLabel')}</label>
            <div className="form-input-wrapper">
              <Lock className="form-input-icon" size={20} />
              <input
                type={showPassword ? 'text' : 'password'}
                className={`form-input ${fieldErrors.password ? 'form-input--error' : ''}`}
                placeholder="••••••••"
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                disabled={loading}
              />
              <button
                type="button"
                className="form-input-toggle"
                onClick={() => setShowPassword(!showPassword)}
                aria-label={showPassword ? t('login.hidePassword') : t('login.showPassword')}
              >
                {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
              </button>
            </div>
            {fieldErrors.password && (
              <p className="form-error">
                <AlertCircle size={12} />
                {fieldErrors.password}
              </p>
            )}
          </div>

          {/* Remember Me & Forgot Password */}
          <div className="form-row">
            <label className="form-checkbox">
              <input
                type="checkbox"
                className="table-checkbox"
                checked={formData.rememberMe}
                onChange={(e) => setFormData({ ...formData, rememberMe: e.target.checked })}
              />
              <span>{t('login.rememberMe')}</span>
            </label>
            <Link to={`/${currentLang}/forgot-password`} className="form-link">
              {t('login.forgotPassword')}
            </Link>
          </div>

          {/* Submit Button */}
          <button
            type="submit"
            className="login-button"
            disabled={loading}
          >
            {loading ? (
              <>
                <span className="login-button-spinner" />
                {t('login.signingIn')}
              </>
            ) : (
              t('login.signIn')
            )}
          </button>
        </form>

        {/* Footer Link */}
        <p className="login-footer">
          {t('login.noAccount')}{' '}
          <Link to={`/${currentLang}/register`} className="login-footer-link">
            {t('login.requestAccess')}
          </Link>
        </p>
      </div>
    </div>
  );
}
